import { Component, EventEmitter, forwardRef, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cloneDeep } from 'lodash';
import { buttonTypeSelectOptions } from '../../../../utils/shared/button/button-type-select-options';
import { ButtonType } from '../../../../utils/button/button-type.enum';
import { CrudFormConfigInputChangeReaction } from '../../../../utils/edit-form/crud-form-config-input-change-reaction.enum';
import { CrudFormConfig } from '../../../../utils/edit-form/crud-form-config.interface';
import { FormLayout } from '../../../../utils/edit-form/form-layout.enum';
import { SINGLE_NOT_SEARCHABLE_SELECT_SETTINGS } from '../../../../utils/select/single-not-searchable-select.settings';
import { defaultCrudViewFormConfig } from './defaults/default-form-config';
import { sizeSelectOptions } from './defaults/size-select-options';
import { UsableCrudViewFormItemType } from './defaults/usable-crud-view-form-item-type.enum';
import { StringUtilityService } from 'libs/shared/src/lib/services/string-utility-service/string-utility.service';
import { HeadingType } from 'libs/components/src/lib/utils/heading';

@Component({
  selector: 'rappider-form-builder',
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => FormBuilderComponent),
      multi: true
    }
  ]
})
export class FormBuilderComponent implements ControlValueAccessor {

  @Output() blur = new EventEmitter<CrudFormConfig>();

  formConfig: CrudFormConfig = defaultCrudViewFormConfig;

  formItems = Object.entries(UsableCrudViewFormItemType).map(([key, value]) => ({ key: StringUtilityService.toTitleCase(key), value: value }));
  selectedItem: string;
  itemToAdd: string;
  tempValue: CrudFormConfig;
  _value: CrudFormConfig;
  isEditModalVisible = false;
  /* holds the JSON editor modal visibility state */
  isJsonEditorModalVisible = false;
  /* holds a backup of data before JSON editor modal is opened */
  jsonModalBackupData: CrudFormConfig;
  scrollToLastItem = false;
  /* holds the initial value passed to component. */
  tempValueWithoutChanges: CrudFormConfig;
  autoSelectedFormItem: string;
  headingConfig = Object.entries(HeadingType).map(([key, value]) => ({
    key: key,
    value: value
  }));

  modalBodyStyle = {
    padding: 0
  };

  /* settings for select component */
  SINGLE_SELECT_SETTINGS = SINGLE_NOT_SEARCHABLE_SELECT_SETTINGS;

  formLayoutOptions = Object.entries(FormLayout).map(([key, value]) => ({
    key: key,
    value: value
  }));

  inputChangeReactions = Object.entries(CrudFormConfigInputChangeReaction).map(([key, value]) => ({
    key: key,
    value: value
  }));

  buttonSizeSelectOptions = sizeSelectOptions;
  buttonTypeSelectOptions = buttonTypeSelectOptions;
  monacoCodeEditorVisibility = false;
  selectedIndex = 0;

  get value() {
    return this._value;
  }

  set value(value: CrudFormConfig) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
    this.blur.emit(this.value);
  }

  constructor() { }

  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(value: CrudFormConfig) {
    if (value) {
      this.tempValue = value;
      if (!this.tempValue.submitButton) {
        this.tempValue.submitButton = {
          visible: true,
          type: ButtonType.Default
        };
      }
      if (!this.tempValue.validators) {
        this.tempValue.validators = [
          {
            name: '',
            validatorKey: ''
          }
        ]
      }
    } else {
      this.tempValue = cloneDeep(this.formConfig);
    }
    this.tempValueWithoutChanges = this.tempValue;
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  onItemsChange() {
    this.tempValue = { ...this.tempValue };
  }

  onFormConfigSave() {
    this.value = this.tempValue;
    this.isEditModalVisible = false;
  }

  onCancel() {
    this.isEditModalVisible = false;
    this.tempValue = cloneDeep(this.tempValueWithoutChanges);
  }

  openEditModal() {
    this.isEditModalVisible = true;
    this.autoSelectedFormItem = null;
  }

  onFormItemSelected(item) {
    this.itemToAdd = item;
  }

  onSelectItem(item) {
    this.selectedItem = item;
    this.selectedIndex = 0;
  }

  selectedTabIndexChange(index) {
    this.selectedIndex = index;
  }

  onScrollToLastItem(data) {
    this.scrollToLastItem = data;
  }

  onScrollToLastItemEmit(data) {
    this.scrollToLastItem = data;
  }

  onAutoSelectFormItem(fieldName) {
    this.autoSelectedFormItem = fieldName;
  }

  /**
   * Opens the JSON editor modal to edit form configuration
   */
  openJsonEditorModal() {
    this.jsonModalBackupData = this.tempValue;
    this.isJsonEditorModalVisible = true;
  }

  /**
   * Closes the JSON editor modal
   */
  closeJsonEditorModal() {
    this.isJsonEditorModalVisible = false;

    if (!this.tempValue) {
      this.tempValue = this.jsonModalBackupData;
    }

    this.value = this.tempValue;
    this.tempValueWithoutChanges = this.tempValue;
  }

  /**
   * Triggered when JSON editor modal visibility changes
   * @param visible Modal visibility state
   */
  onJsonEditorVisibleChange(visible: boolean) {
    this.isJsonEditorModalVisible = visible;

    if (!visible) {
      if (!this.tempValue) {
        this.tempValue = this.jsonModalBackupData;
      }

      this.value = this.tempValue;
      this.tempValueWithoutChanges = this.tempValue;
    }
  }

  /**
   * Triggered when JSON data changes in the editor
   * @param event Updated JSON data
   */
  onDataChange(event: any) {
    if (event) {
      this.tempValue = cloneDeep(event);
      this.value = cloneDeep(this.tempValue);
    }
  }
}
